
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { categories } from "@/lib/data/categories";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import AnimatedHeadline from "@/components/ui/AnimatedHeadline";

export default function CategoryMultiverse() {
  return (
    <section id="categories" className="relative py-24 md:py-32 overflow-hidden">
      <div className="nm-starfield nm-starfield-animated absolute inset-0 opacity-30" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(27,114,238,.2),transparent_42%)]" />
      <div className="nm-container">
        <RevealOnScroll>
          <span className="nm-kicker">Choose your universe</span>
          <AnimatedHeadline
            as="h2"
            text="EXPLORE EVERY FANDOM."
            className="mt-5 font-display text-4xl md:text-6xl font-black text-white tracking-[-0.045em] max-w-3xl"
          />
          <p className="mt-4 text-[var(--nm-ink-muted)] max-w-xl">Ten portals. Thousands of possibilities. Start with the corner of the multiverse that feels like home.</p>
        </RevealOnScroll>

        <div className="relative mt-14 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 md:gap-5 xl:grid-cols-4">
          {categories.map((cat, i) => (
            <RevealOnScroll
              key={cat.slug}
              delay={(i % 4) * 0.06}
            >
              <Link
                to={`/marketplace?category=${cat.slug}`}
                className="group relative flex h-full w-full flex-col overflow-hidden rounded-[1.5rem] border border-cyan-100/15 bg-[#071a40] shadow-[0_16px_42px_rgba(0,8,32,.3)] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-yellow-200/45 hover:shadow-[0_22px_55px_rgba(0,75,180,.35)]"
              >
                <div className="nm-grain relative aspect-square w-full flex-none overflow-hidden bg-[#0b2354] md:aspect-[3/2]">
                  <picture className="absolute inset-0 block h-full w-full">
                    <source
                      media="(min-width: 768px)"
                      srcSet={`/images/categories/${cat.slug}-desktop-v3.webp`}
                    />
                    <motion.img
                      src={cat.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.025 }}
                      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0 h-full w-full object-cover saturate-[.9] brightness-[.9] contrast-[1.03] transition-[filter] duration-500 group-hover:saturate-100 group-hover:brightness-100"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#061631]/70 via-transparent to-[#0a2550]/18" />
                  <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-[#03112b]/70 px-3 py-1.5 text-[9px] font-black tracking-[.2em] text-white/70 backdrop-blur-md">
                    PORTAL {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="relative flex min-h-[96px] items-center justify-between gap-4 border-t border-white/10 bg-[linear-gradient(135deg,rgba(11,40,91,.98),rgba(4,17,48,.98))] px-5 py-4">
                  <div className="min-w-0">
                    <h3 className="font-display text-xl font-black tracking-tight text-white">{cat.name}</h3>
                    <p className="mt-1 truncate text-xs text-[var(--nm-ink-muted)]">{cat.tagline}</p>
                  </div>
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    style={{ color: cat.accent }}
                  >
                    <ArrowUpRight size={17} />
                  </span>
                </div>
              </Link>
            </RevealOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}
